import { getIdToken } from "./auth";
import type {
  AdminUser,
  AgentsResponse,
  AuditQuery,
  AuditResponse,
  ListUsersResponse,
  NewNote,
  NotesResponse,
  PlaybackResponse,
  RecordingNote,
  SearchParams,
  SearchResponse,
  TroubleshootResult,
  UserGroup,
} from "./types";

const apiBase = import.meta.env.VITE_API_BASE_URL;

if (!apiBase) {
  // eslint-disable-next-line no-console
  console.error("VITE_API_BASE_URL missing at build time");
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body}`);
  }
}

function authHeader(): Record<string, string> {
  const token = getIdToken();
  if (!token) throw new ApiError(401, "not authenticated");
  return { Authorization: `Bearer ${token}` };
}

export async function search(params: SearchParams): Promise<SearchResponse> {
  const url = new URL(`${apiBase}/recordings/search`);
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") url.searchParams.set(k, String(v));
  }
  const res = await fetch(url.toString(), { headers: authHeader() });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

export async function getPlaybackUrl(contactId: string, reason?: string): Promise<PlaybackResponse> {
  const url = new URL(`${apiBase}/recordings/${encodeURIComponent(contactId)}/playback-url`);
  if (reason && reason.trim()) url.searchParams.set("reason", reason.trim());
  const res = await fetch(url.toString(), { headers: authHeader() });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

// ----- Admin user-management API (admins group only) -----

export async function listUsers(paginationToken?: string): Promise<ListUsersResponse> {
  const url = new URL(`${apiBase}/admin/users`);
  if (paginationToken) url.searchParams.set("paginationToken", paginationToken);
  const res = await fetch(url.toString(), { headers: authHeader() });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

export async function addUserToGroup(username: string, groupName: UserGroup): Promise<void> {
  const url = `${apiBase}/admin/users/${encodeURIComponent(username)}/groups`;
  const res = await fetch(url, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ groupName }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export async function removeUserFromGroup(username: string, groupName: UserGroup): Promise<void> {
  const url = `${apiBase}/admin/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`;
  const res = await fetch(url, { method: "DELETE", headers: authHeader() });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export async function disableUser(username: string): Promise<void> {
  const url = `${apiBase}/admin/users/${encodeURIComponent(username)}/disable`;
  const res = await fetch(url, { method: "POST", headers: authHeader() });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

// ----- Helper fleet status (supervisors + admins) -----

export async function getAgents(): Promise<AgentsResponse> {
  const url = `${apiBase}/admin/agents`;
  const res = await fetch(url, { headers: authHeader() });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

// ----- Playback audit log (supervisors + admins) -----

export async function getAudit(params: AuditQuery): Promise<AuditResponse> {
  const url = new URL(`${apiBase}/admin/audit`);
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") url.searchParams.set(k, String(v));
  }
  const res = await fetch(url.toString(), { headers: authHeader() });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

// ----- Recording notes (coaching / QA) -----

export async function listNotes(contactId: string): Promise<NotesResponse> {
  const url = `${apiBase}/recordings/${encodeURIComponent(contactId)}/notes`;
  const res = await fetch(url, { headers: authHeader() });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

export async function addNote(contactId: string, body: NewNote): Promise<RecordingNote> {
  const url = `${apiBase}/recordings/${encodeURIComponent(contactId)}/notes`;
  const res = await fetch(url, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text).note;
}

export async function deleteNote(contactId: string, noteId: string): Promise<void> {
  const url = `${apiBase}/recordings/${encodeURIComponent(contactId)}/notes/${encodeURIComponent(noteId)}`;
  const res = await fetch(url, { method: "DELETE", headers: authHeader() });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

// ----- Helper-log troubleshooting (Bedrock triage; supervisors + admins) -----

export async function troubleshoot(logText: string): Promise<TroubleshootResult> {
  const url = `${apiBase}/admin/troubleshoot`;
  const res = await fetch(url, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ logText }),
  });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

export type { AdminUser };
